import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * "Add to cart" on OTC product cards (You may also like, Recently viewed).
 * Adds the card's first available variant straight to the cart. Once the item is in
 * the cart the button becomes a − qty + stepper for that cart line (0 removes it and
 * the button comes back). Cart changes are announced the way Horizon's product form
 * does, so the cart icon, drawer and cart page stay in sync.
 */

const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

/** One /cart.js read per page, shared by every card. */
let cartPromise;
const readCart = (fresh = false) => {
  if (fresh || !cartPromise) {
    cartPromise = fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return cartPromise;
};

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

if (!customElements.get('otc-card-add')) {
  customElements.define(
    'otc-card-add',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.otc-card-add__button');
        this.label = this.querySelector('.otc-card-add__label');
        this.stepper = this.querySelector('.otc-card-add__stepper');
        this.qty = this.querySelector('.otc-card-add__qty');
        this.button?.addEventListener('click', this.onAdd);
        this.querySelector('[data-step="-1"]')?.addEventListener('click', this.onStep);
        this.querySelector('[data-step="1"]')?.addEventListener('click', this.onStep);
        document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
        if (this.dataset.variantId && !this.button?.disabled) {
          readCart().then((cart) => this.sync(cart));
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onAdd);
        document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
      }

      /** Show the stepper when this variant is in the cart, the button when it isn't. */
      sync(cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        const line = cart.items.find((item) => String(item.variant_id) === String(this.dataset.variantId));
        this.lineKey = line ? line.key : null;
        this.quantity = line ? line.quantity : 0;
        this.render();
      }

      render() {
        const inCart = this.quantity > 0;
        this.classList.toggle('is-in-cart', inCart);
        if (this.button) this.button.hidden = inCart;
        if (this.stepper) this.stepper.hidden = !inCart;
        if (this.qty) this.qty.textContent = String(this.quantity || 0);
      }

      /** Keep in step with changes made elsewhere (cart drawer, other cards, product form). */
      onCartUpdate = (event) => {
        if (event.detail?.sourceId === this.eventSourceId) return;
        const cart = event.detail?.resource;
        if (cart && Array.isArray(cart.items)) {
          this.sync(cart);
        } else {
          readCart(true).then((fresh) => this.sync(fresh));
        }
      };

      /** Unique per card, so two cards for the same product still update each other. */
      get eventSourceId() {
        if (!this._sourceId) this._sourceId = `otc-card-add-${this.dataset.variantId}-${Math.random().toString(36).slice(2, 8)}`;
        return this._sourceId;
      }

      onAdd = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const variantId = this.dataset.variantId;
        if (!variantId || this.button.disabled || this.busy) return;
        await this.request(this.button, async () => {
          const sections = cartSectionIds();
          const response = await fetch(`${root()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1 }],
              ...(sections.length ? { sections: sections.join(',') } : {}),
            }),
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'add failed');

          const cart = await readCart(true);
          this.sync(cart);
          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, this.eventSourceId, {
              source: 'product-form-component',
              itemCount: 1,
              productId: this.dataset.productId,
              variantId: String(variantId),
              sections: data.sections,
            })
          );
          this.announce(this.dataset.addedText || 'Added ✓');
          // The button is now hidden; keep keyboard focus on the stepper
          if (this.quantity > 0) this.querySelector('[data-step="1"]')?.focus();
        });
      };

      onStep = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.busy || !this.lineKey) return;
        const step = Number(event.currentTarget.dataset.step) || 0;
        const quantity = Math.max(0, (this.quantity || 0) + step);
        await this.request(event.currentTarget, async () => {
          const sections = cartSectionIds();
          const response = await fetch(`${root()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: this.lineKey,
              quantity,
              ...(sections.length ? { sections: sections.join(',') } : {}),
            }),
          });
          const cart = await response.json();
          if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'change failed');

          cartPromise = Promise.resolve(cart);
          this.sync(cart);
          this.dispatchEvent(
            new CartUpdateEvent(cart, this.eventSourceId, {
              source: 'otc-card-add',
              itemCount: cart.item_count,
              productId: this.dataset.productId,
              variantId: String(this.dataset.variantId),
              sections: cart.sections,
            })
          );
          if (quantity === 0) {
            this.announce(this.dataset.removedText || 'Removed from cart');
            this.button?.focus();
          } else if (this.status) {
            this.status.textContent = (this.dataset.inCartText || '[count] in cart').replace('[count]', String(quantity));
          }
        });
      };

      async request(control, work) {
        this.busy = true;
        this.setAttribute('aria-busy', 'true');
        try {
          await work();
        } catch (error) {
          this.announce(this.dataset.errorText || "Couldn't update your cart", true);
          readCart(true).then((cart) => this.sync(cart));
        } finally {
          this.busy = false;
          this.removeAttribute('aria-busy');
        }
      }

      /** Brief confirmation in whichever control is showing (the qty pill or the button), plus a live region. */
      announce(text, isError = false) {
        const inCart = this.quantity > 0;
        const target = inCart ? this.qty : this.label;
        if (this.status) this.status.textContent = text;
        if (target) target.textContent = text;
        this.classList.toggle('is-added', !isError);
        this.classList.toggle('is-error', isError);
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          if (this.qty) this.qty.textContent = String(this.quantity || 0);
          if (this.label) this.label.textContent = this.dataset.addText || 'Add to cart';
          if (this.status) this.status.textContent = '';
          this.classList.remove('is-added', 'is-error');
        }, 1500);
      }

      get status() {
        return this.querySelector('.otc-card-add__status');
      }
    }
  );
}
